import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useAuthentication } from './AuthenticationContext'
import useTeamsResources from '../shared/resources/api/v1/admin/useTeamsResources'

const TeamsContext = createContext(null)

export function TeamsProvider({ children }) {
  const { isAuthenticated } = useAuthentication()
  const { all } = useTeamsResources()
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(isAuthenticated)
  const [error, setError] = useState(null)
  const loadingRef = useRef(false)

  const loadTeams = useCallback(() => {
    if (loadingRef.current) {
      return Promise.resolve([])
    }
    loadingRef.current = true
    setLoading(true)
    setError(null)
    return all()
      .then((response) => {
        setTeams(response.teams)
        return response.teams
      })
      .catch((err) => {
        setError(err)
        throw err
      })
      .finally(() => {
        setLoading(false)
        loadingRef.current = false
      })
  }, [all])

  useEffect(() => {
    if (isAuthenticated) {
      loadTeams()
    } else {
      setTeams([])
      setError(null)
    }
  }, [isAuthenticated])

  const value = useMemo(() => ({
    teams,
    loading,
    error,
    loadTeams,
  }), [teams, loading, error, loadTeams])

  return (
    <TeamsContext.Provider value={value}>
      {children}
    </TeamsContext.Provider>
  )
}

export function useTeams() {
  const context = useContext(TeamsContext)
  if (!context) {
    throw new Error('useTeams must be used within a TeamsProvider')
  }
  return context
}
